import { useState, useEffect } from "react";
import AdminLayout from "@/components/AdminLayout";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Loader2,
  Search,
  X,
  Crown,
  Clock,
  Activity,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

interface UserRow {
  user_id: string;
  full_name: string | null;
  sport_name: string | null;
  onboarding_completed: boolean;
  created_at: string;
  session_count: number;
  viz_count: number;
  subscription_status: string | null;
  plan_name: string | null;
  trial_end: string | null;
  weekly_credits_used: number;
  weekly_credits_limit: number;
  last_session_at: string | null;
  email: string | null;
}

const AdminUsersPage = () => {
  const [users, setUsers] = useState<UserRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [expandedUser, setExpandedUser] = useState<string | null>(null);
  const [filter, setFilter] = useState<string>("all");

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      // Fetch profiles with sport names
      const { data: profiles, error: profilesError } = await supabase
        .from("profiles")
        .select("user_id, full_name, onboarding_completed, created_at, sport_id, sports(name)")
        .order("created_at", { ascending: false });

      if (profilesError) throw profilesError;

      // Fetch all subscriptions
      const { data: subs } = await supabase
        .from("subscriptions")
        .select("user_id, status, plan_name, trial_end, weekly_credits_used, weekly_credits_limit");

      // Fetch viz counts per user
      const { data: vizData } = await supabase
        .from("visualizations")
        .select("user_id, id");

      // Fetch session counts and last session
      const { data: sessionData } = await supabase
        .from("completed_sessions")
        .select("user_id, completed_at")
        .order("completed_at", { ascending: false });

      // Build lookup maps
      const subMap = new Map<string, any>();
      subs?.forEach((s) => subMap.set(s.user_id, s));

      const vizCountMap = new Map<string, number>();
      vizData?.forEach((v) => {
        vizCountMap.set(v.user_id, (vizCountMap.get(v.user_id) || 0) + 1);
      });

      const sessionCountMap = new Map<string, number>();
      const lastSessionMap = new Map<string, string>();
      sessionData?.forEach((s) => {
        sessionCountMap.set(s.user_id, (sessionCountMap.get(s.user_id) || 0) + 1);
        if (!lastSessionMap.has(s.user_id)) lastSessionMap.set(s.user_id, s.completed_at);
      });

      const rows: UserRow[] = (profiles || []).map((p: any) => {
        const sub = subMap.get(p.user_id);
        return {
          user_id: p.user_id,
          full_name: p.full_name,
          sport_name: p.sports?.name || null,
          onboarding_completed: p.onboarding_completed,
          created_at: p.created_at,
          session_count: sessionCountMap.get(p.user_id) || 0,
          viz_count: vizCountMap.get(p.user_id) || 0,
          subscription_status: sub?.status || null,
          plan_name: sub?.plan_name || null,
          trial_end: sub?.trial_end || null,
          weekly_credits_used: sub?.weekly_credits_used || 0,
          weekly_credits_limit: sub?.weekly_credits_limit || 0,
          last_session_at: lastSessionMap.get(p.user_id) || null,
          email: null, // We can't access auth.users from client
        };
      });

      setUsers(rows);
    } catch (err: any) {
      console.error("Failed to load users:", err);
      toast.error("Failed to load user data");
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string | null, trialEnd: string | null) => {
    if (!status)
      return (
        <span className="px-2 py-0.5 rounded-full text-xs bg-secondary/50 text-muted-foreground">
          No subscription
        </span>
      );

    const isTrialActive =
      status === "trialing" && trialEnd && new Date(trialEnd) > new Date();

    const colors: Record<string, string> = {
      active: "bg-green-500/20 text-green-400",
      trialing: "bg-blue-500/20 text-blue-400",
      canceled: "bg-yellow-500/20 text-yellow-400",
      past_due: "bg-red-500/20 text-red-400",
      expired: "bg-secondary/50 text-muted-foreground",
    };

    return (
      <span
        className={`px-2 py-0.5 rounded-full text-xs font-medium ${
          colors[status] || colors.expired
        }`}
      >
        {isTrialActive ? "Trial" : status.charAt(0).toUpperCase() + status.slice(1)}
        {isTrialActive && trialEnd && (
          <span className="ml-1 opacity-70">
            (ends {new Date(trialEnd).toLocaleDateString()})
          </span>
        )}
      </span>
    );
  };

  const filtered = users.filter((u) => {
    const matchesSearch =
      !search ||
      u.full_name?.toLowerCase().includes(search.toLowerCase()) ||
      u.user_id.toLowerCase().includes(search.toLowerCase());

    if (filter === "all") return matchesSearch;
    if (filter === "subscribed") return matchesSearch && (u.subscription_status === "active" || u.subscription_status === "trialing");
    if (filter === "trial") return matchesSearch && u.subscription_status === "trialing";
    if (filter === "no-sub") return matchesSearch && !u.subscription_status;
    if (filter === "active") return matchesSearch && u.session_count > 0;
    return matchesSearch;
  });

  const totalSubscribed = users.filter(
    (u) => u.subscription_status === "active" || u.subscription_status === "trialing"
  ).length;
  const totalTrialing = users.filter((u) => u.subscription_status === "trialing").length;
  const totalSessions = users.reduce((acc, u) => acc + u.session_count, 0);

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="mb-8">
        <h1 className="font-display text-3xl font-bold mb-2">User Management</h1>
        <p className="text-muted-foreground">
          {users.length} total users • {totalSubscribed} subscribed • {totalTrialing} trialing • {totalSessions} sessions
        </p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        {[
          { label: "Total Users", value: users.length, icon: Activity },
          { label: "Subscribed", value: totalSubscribed, icon: Crown },
          { label: "In Trial", value: totalTrialing, icon: Clock },
          { label: "Total Sessions", value: totalSessions, icon: Activity },
        ].map((stat) => (
          <div
            key={stat.label}
            className="gradient-card rounded-xl border border-border/50 p-4 shadow-card"
          >
            <stat.icon className="w-4 h-4 text-primary mb-2" />
            <div className="font-display text-xl font-bold">{stat.value}</div>
            <div className="text-xs text-muted-foreground">{stat.label}</div>
          </div>
        ))}
      </div>

      {/* Search and filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search by name or ID..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full h-10 pl-10 pr-4 rounded-xl border border-border/50 bg-secondary/30 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring text-sm"
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              className="absolute right-3 top-1/2 -translate-y-1/2"
            >
              <X className="w-4 h-4 text-muted-foreground" />
            </button>
          )}
        </div>
        <div className="flex gap-2 flex-wrap">
          {[
            { key: "all", label: "All" },
            { key: "subscribed", label: "Subscribed" },
            { key: "trial", label: "Trial" },
            { key: "no-sub", label: "No Sub" },
            { key: "active", label: "Has Sessions" },
          ].map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                filter === f.key
                  ? "bg-primary/10 text-primary border border-primary/30"
                  : "bg-secondary/30 text-muted-foreground border border-border/50 hover:border-primary/20"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Users list */}
      <div className="space-y-2">
        {filtered.length === 0 ? (
          <div className="text-center py-16 gradient-card rounded-xl border border-border/50">
            <p className="text-muted-foreground">No users match your filters.</p>
          </div>
        ) : (
          filtered.map((u) => (
            <div
              key={u.user_id}
              className="gradient-card rounded-xl border border-border/50 shadow-card overflow-hidden"
            >
              <button
                onClick={() =>
                  setExpandedUser(expandedUser === u.user_id ? null : u.user_id)
                }
                className="w-full p-4 flex items-center gap-4 text-left hover:bg-secondary/20 transition-colors"
              >
                {/* Avatar */}
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <span className="font-display text-sm font-bold text-primary">
                    {u.full_name
                      ? u.full_name
                          .split(" ")
                          .map((n) => n[0])
                          .join("")
                          .slice(0, 2)
                          .toUpperCase()
                      : "?"}
                  </span>
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-sm truncate">
                      {u.full_name || "Unnamed User"}
                    </span>
                    {getStatusBadge(u.subscription_status, u.trial_end)}
                    {u.plan_name && (
                      <span className="px-2 py-0.5 rounded-full text-xs bg-primary/10 text-primary font-medium">
                        {u.plan_name}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                    {u.sport_name && <span>{u.sport_name}</span>}
                    <span>{u.viz_count} viz</span>
                    <span>{u.session_count} sessions</span>
                    <span>Joined {new Date(u.created_at).toLocaleDateString()}</span>
                  </div>
                </div>

                {expandedUser === u.user_id ? (
                  <ChevronUp className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                )}
              </button>

              {expandedUser === u.user_id && (
                <div className="px-4 pb-4 pt-1 border-t border-border/30">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    <div>
                      <span className="text-xs text-muted-foreground block">User ID</span>
                      <span className="font-mono text-xs break-all">{u.user_id}</span>
                    </div>
                    <div>
                      <span className="text-xs text-muted-foreground block">Credits Used</span>
                      <span>
                        {u.weekly_credits_used} / {u.weekly_credits_limit === -1 ? "∞" : u.weekly_credits_limit}
                      </span>
                    </div>
                    <div>
                      <span className="text-xs text-muted-foreground block">Last Session</span>
                      <span>
                        {u.last_session_at
                          ? new Date(u.last_session_at).toLocaleDateString()
                          : "Never"}
                      </span>
                    </div>
                    <div>
                      <span className="text-xs text-muted-foreground block">Onboarding</span>
                      <span>{u.onboarding_completed ? "✅ Completed" : "❌ Incomplete"}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminUsersPage;
